import { useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useGetAffidavitsForWorkOrder, type Affidavit, useDownloadAffidavitPDF } from '@/services/affidavitService';
import Loader from '@/components/Loader';
import { downloadFilefromBuffer } from '@/lib/utils';
import { displayError } from '@/lib/error';
import type { AxiosError } from 'axios';
import type { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';

type AffidavitMode = 'blank' | 'complete';

interface AffidavitsDetailsPeContentProps {
  workOrderId?: string;
  workOrderNumber?: string;
  exportCompleteAffidavit: boolean;
}

const AffidavitsDetailsPeContent = ({
  workOrderId,
  workOrderNumber,
  exportCompleteAffidavit,
}: AffidavitsDetailsPeContentProps) => {
  const { data: affidavits, isLoading, isError } = useGetAffidavitsForWorkOrder(workOrderId);
  const [selectedAffidavit, setSelectedAffidavit] = useState<Affidavit | null>(null);
  const [affidavitMode, setAffidavitMode] = useState<AffidavitMode>('blank');
  const [isDownloading, setIsDownloading] = useState(false);
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const downloadAffidavitPDFMutation = useDownloadAffidavitPDF();
  const handleAffidavitClick = (affidavit: Affidavit) => {
    setSelectedAffidavit(affidavit);
    setAffidavitMode('blank');
    setModeDialogOpen(true);
  };

  const handleDownload = async () => {
    if (!selectedAffidavit || !workOrderId) return;

    setIsDownloading(true);

    await downloadAffidavitPDFMutation.mutateAsync(
      {
        workOrderId,
        affidavitCode: selectedAffidavit.code,
        blankTemplate: affidavitMode === 'blank',
      },
      {
        onSuccess: (data) => {
          downloadFilefromBuffer(
            data,
            `${workOrderNumber}_${(selectedAffidavit.title || selectedAffidavit.name).replace(/ /g, '_')}.pdf`,
            'PDF'
          );
          setIsDownloading(false);
          setModeDialogOpen(false);
        },
        onError: async (error: AxiosError<GenericErrorResponse>) => {
          const errorData = error.response?.data;
          if (errorData instanceof Blob) {
            const text = await errorData.text();
            try {
              const parsedError = JSON.parse(text) as GenericErrorResponse;
              displayError(parsedError);
            } catch {
              displayError({ error: { message: 'Failed to download PDF' } });
            }
          } else {
            displayError(errorData);
          }
          setIsDownloading(false);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 w-full px-6 py-6">
        <Loader isLoading={true}>
          <div className="h-40" />
        </Loader>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
        <div className="w-full justify-center flex font-medium text-error">Failed to load affidavits</div>
      </div>
    );
  }

  if (!affidavits || affidavits.length === 0) {
    return (
      <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
        <div className="w-full justify-center flex font-medium text-placeholder">No affidavits available</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full px-6 py-6 text-sm">
      <span className="text-black text-xs">Choose one affidavit to export.</span>

      <div className="flex flex-col gap-2">
        {affidavits.map((affidavit) => (
          <div
            key={affidavit.code}
            className="flex flex-row items-center justify-between p-4 rounded-lg bg-[#F5F5F5] hover:bg-[#EBEBEB] cursor-pointer transition-colors"
            onClick={() => handleAffidavitClick(affidavit)}
          >
            <div className="flex flex-row items-center gap-3">
              <div>
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div className="font-medium text-sm text-primary break-words">{affidavit.title || affidavit.name}</div>
            </div>
            <div>
              <Download className="w-5 h-5 text-primary" />
            </div>
          </div>
        ))}
      </div>

      <Dialog open={modeDialogOpen} onOpenChange={setModeDialogOpen}>
        <DialogContent className="max-w-[50vw]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Select Affidavit Mode for {selectedAffidavit ? selectedAffidavit.title ?? selectedAffidavit.name : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4">
            <RadioGroup
              value={affidavitMode}
              onValueChange={(value) => setAffidavitMode(value as AffidavitMode)}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="blank" id="blank" />
                <Label htmlFor="blank" className="cursor-pointer">
                  Blank Affidavit - Without Attempt Details
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="complete" id="complete" disabled={!exportCompleteAffidavit} />
                <Label
                  htmlFor="complete"
                  className={`${!exportCompleteAffidavit ? 'text-placeholder cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  Complete Affidavit - With Attempt Details
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              className="h-[32px]"
              onClick={() => setModeDialogOpen(false)}
              disabled={isDownloading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDownload}
              disabled={isDownloading}
              isLoading={isDownloading}
              className="h-[32px] w-[120px]"
            >
              Export PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffidavitsDetailsPeContent;
